import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@column/ui';

import { LabeledOption } from '../_util/entityOptions';
import { IdentificationDocument, PersonEntityFormValues } from '../_util/useEntityForm';
import { FormField, HiddenInput } from '~/components';
import { Grid } from '~/styles';

// Non-U.S. identification documents, in the order the person form has always shown them. The
// backend rejects a U.S. issuing country for each, so those options are filtered out of the picker.
const FOREIGN_DOCS: {
  id: 'driversLicense' | 'passport' | 'nationalId';
  label: string;
  placeholder: string;
  excludeCountries: string[];
}[] = [
  {
    id: 'driversLicense',
    label: 'Drivers License',
    placeholder: 'Drivers License Number',
    excludeCountries: ['US', 'CA'],
  },
  { id: 'passport', label: 'Passport', placeholder: 'Passport Number', excludeCountries: ['US'] },
  { id: 'nationalId', label: 'National ID', placeholder: 'National ID Number', excludeCountries: ['US', 'CA'] },
];

// The identification portion of the person form: a U.S. citizen supplies an SSN/ITIN, while a
// non-citizen supplies at least one foreign document. Switching the citizenship answer animates the
// relevant group open and the other closed. Shared by the entity create/edit page and the
// link-new-person modal so both behave identically.
export const PersonIdentificationFields: React.FC<{
  isCitizen: boolean;
  countryOptions: LabeledOption[];
  disabled?: boolean;
  // SSN is shown in the clear while creating (nothing to hide yet) and masked by default on edit.
  ssnDefaultVisible?: boolean;
}> = ({ isCitizen, countryOptions, disabled, ssnDefaultVisible }) => {
  const form = useFormContext<PersonEntityFormValues>();

  return (
    <>
      <Accordion type="single" value={isCitizen ? 'ssn' : ''}>
        <AccordionItem value="ssn" className="border-b-0">
          <AccordionContent className="pb-0">
            <FormField<PersonEntityFormValues>
              id="ssn"
              label="Social Security Number"
              description="ITIN is also acceptable."
              rules={{ required: isCitizen }}
            >
              {({ value, onChange, onBlur, ref }) => (
                <HiddenInput
                  value={(value as string) ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  placeholder="Social Security Number"
                  defaultIsVisible={ssnDefaultVisible}
                  isDisabled={disabled}
                  readOnly={false}
                  showCopy={false}
                  visibilityLabel="Social Security Number"
                />
              )}
            </FormField>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" value={!isCitizen ? 'documents' : ''}>
        <AccordionItem value="documents" className="border-b-0">
          <AccordionContent className="pb-0">
            {/* Single column so each field keeps its full width — a full-width FormField is what trips
                the container query that puts the label to the left of the input. Grid defaults to two
                columns, which would halve the width and stack the labels on top. */}
            <Grid gap={24} vertical>
              {FOREIGN_DOCS.map((doc) => (
                <FormField<PersonEntityFormValues> key={doc.id} id={doc.id} label={doc.label}>
                  {({ value, onBlur }) => (
                    <Grid gap={8}>
                      <Input
                        className="w-full"
                        value={(value as IdentificationDocument)?.number ?? ''}
                        onChange={(event) => form.setValue(`${doc.id}.number`, event.target.value)}
                        onBlur={onBlur}
                        placeholder={doc.placeholder}
                        disabled={disabled}
                      />
                      <Select
                        value={(value as IdentificationDocument)?.countryCode ?? ''}
                        onValueChange={(val) => form.setValue(`${doc.id}.countryCode`, val)}
                        disabled={disabled}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Country..." />
                        </SelectTrigger>
                        <SelectContent>
                          {countryOptions
                            .filter((c) => !doc.excludeCountries.includes(c.value))
                            .map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </Grid>
                  )}
                </FormField>
              ))}
            </Grid>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
